/**
 * The exact body the platform POSTs to a template's executionUrl, for endpoint developers. Keep in step with
 * processedModelExecutorFunction (FORECAST: src/index.js, src/modelOutputInputs.js) and historicalModelRunFunction
 * (HISTORICAL: src/handler.ts, src/datasets.ts, src/outputs.ts). Run-time values are placeholders.
 */

import {
  activeInputFiles,
  addDuration,
  datasetIncomingKey,
  datasetUploadMetadata,
  inputIssues,
  parseDuration,
  toIso,
  uploadIdFor,
  windowFor,
} from "./historicalRuns";
import type {
  DatasetFileRow,
  DatasetLinkRow,
  ForecastRecordRow,
  HistoricalRunConfig,
  InputFile,
  InputIssue,
  OutputLinkRow,
  TimeWindow,
} from "./historicalRuns";

interface PostSampleAsset {
  id: string;
  name: string;
  lat: number | null;
  lng: number | null;
  geojson?: string | null;
}

interface PostSampleLinkedAsset {
  id: string;
  assetID: string;
  asset: PostSampleAsset | null;
}

interface PostSampleLinkedAssetGroup {
  id: string;
  assetGroupID: string;
  assetGroup: {
    id: string;
    name: string;
    assets: PostSampleAsset[];
  } | null;
}

export interface ExecutionPostSample {
  processedModelSchemaId: string;
  schemaType: string;
  version: string;
  schemaFields: Record<string, unknown>;
  outputs: Array<Record<string, unknown>>;
  inputs: Record<string, unknown>;
  linkedAssets: PostSampleLinkedAsset[];
  linkedAssetGroups: PostSampleLinkedAssetGroup[];
  dataSources: Array<Record<string, unknown>>;
  s3BasePath: string;
  s3Bucket: string;
  postgresSeriesFunction: string;
  runId: string;
  owner: string | null;
  organizationId: string | null;
}

export interface HistoricalPostSample extends Omit<ExecutionPostSample, "outputs"> {
  modelKind: "HISTORICAL";
  window: TimeWindow;
  trigger: { type: string; ref: string | null; requestId: string };
  attempt: number;
  inputsReadAt: string;
  outputs: Array<Record<string, unknown>>;
}

/** Values the run functions only know while a run is happening. */
export const POST_SAMPLE_PLACEHOLDERS = {
  runId: "<uuid-generated-at-runtime>",
  s3Bucket: "<storage-bucket-name-injected-at-runtime>",
  postgresSeriesFunction: "postgresSeriesReadFunction-<env>",
  url: "<presigned-https-GET-url-made-at-runtime>",
  scheduleId: "<processedModelScheduleId>",
  requestId: "<processedModelRunRequestId>",
  ownerSub: "<owner-cognito-sub>",
} as const;

/** The forecast executor's defaults for a model_output field (modelOutputInputs.js). */
export const FORECAST_INPUT_DEFAULTS = { lookback: "P7D", latestIssues: 1 } as const;

// Mirrors the executor's safeParse: AWSJSON string -> object, tolerating bad input.
const safeParse = (value: unknown, fallback = {}) => {
  if (value == null) return fallback;
  if (typeof value === "object") return value as Record<string, unknown>;
  try {
    return JSON.parse(value as string);
  } catch {
    return fallback;
  }
};

const flattenAssetGroups = (schema: any): PostSampleLinkedAssetGroup[] =>
  (schema.linkedAssetGroups?.items || []).map((g: any) => ({
    ...g,
    assetGroup: g.assetGroup ? { ...g.assetGroup, assets: g.assetGroup.assets?.items || [] } : null,
  }));

/** The forecast executor's body for a schema fetched with getProcessedModelSchemaForPostSample, plus its inputs. */
export const buildExecutionPostSample = (schema: any, inputs: Record<string, unknown> = {}): ExecutionPostSample => ({
  processedModelSchemaId: schema.id,
  schemaType: schema.schemaType,
  version: schema.version,
  schemaFields: safeParse(schema.schemaFields),
  outputs: (schema.outputs?.items || []).map((o: any) => ({
    id: o.id,
    name: o.name,
    unit: o.unit,
    channels: o.channels,
    parameterTypeId: o.parameterTypeId,
    outputLocation: o.outputLocation,
  })),
  inputs,
  linkedAssets: schema.linkedAssets?.items || [],
  linkedAssetGroups: flattenAssetGroups(schema),
  dataSources: schema.dataSources?.items || [],
  s3BasePath: schema.s3BasePath,
  s3Bucket: POST_SAMPLE_PLACEHOLDERS.s3Bucket,
  postgresSeriesFunction: POST_SAMPLE_PLACEHOLDERS.postgresSeriesFunction,
  runId: POST_SAMPLE_PLACEHOLDERS.runId,
  owner: schema.owner ?? null,
  organizationId: schema.organizationId ?? null,
});

/** The window a scheduled run of a HISTORICAL model covers when it has no watermark: [now − defaultLookback, now]. */
export function sampleHistoricalWindow(config: HistoricalRunConfig, now: Date): TimeWindow {
  const { backfillFrom: _backfillFrom, ...scheduled } = config;
  return windowFor({ now: now.toISOString(), config: scheduled });
}

/** A model_output field's lookback and issue count on a FORECAST model, as the executor reads them. */
export function forecastInputConfigOf(
  fieldDefinitions: Record<string, any>,
  fieldKey: string
): { lookback: string; latestIssues: number } {
  const config = fieldDefinitions?.[fieldKey]?.validation?.modelOutputConfig || {};
  let lookback: string = FORECAST_INPUT_DEFAULTS.lookback;
  try {
    parseDuration(String(config.lookback));
    lookback = String(config.lookback);
  } catch {
    // an unparsable or missing setting keeps the default, as the executor does
  }
  const latestIssues =
    Number.isInteger(config.latestIssues) && config.latestIssues > 0 ? config.latestIssues : FORECAST_INPUT_DEFAULTS.latestIssues;
  return { lookback, latestIssues };
}

/** The [now − lookback, now] window a FORECAST model's model_output input declares. */
export function forecastInputWindow(lookback: string, now: Date): TimeWindow {
  return { start: toIso(addDuration(now.getTime(), parseDuration(lookback), -1)), end: toIso(now.getTime()) };
}

/** One `dataset` input as historicalModelRunFunction's datasetInputs builds it, urls as placeholders. */
export function buildDatasetInputSample(args: {
  link: Pick<DatasetLinkRow, "fieldKey" | "datasetId" | "columns">;
  dataset: { id: string; routeLong?: string | null; ingestMetadata?: unknown };
  fileRows: DatasetFileRow[];
  reach: TimeWindow;
}): Record<string, unknown> {
  const { link, dataset, fileRows, reach } = args;
  const files: Array<InputFile & { url: string }> = activeInputFiles(
    fileRows,
    reach,
    dataset.routeLong ? `${dataset.routeLong}curated/` : ""
  ).map((file) => ({ ...file, url: POST_SAMPLE_PLACEHOLDERS.url }));
  const declared = safeParse(dataset.ingestMetadata) as { columns?: { name?: string; label?: string; unit?: string }[] };
  const columnInfo = Object.fromEntries(
    (declared.columns ?? [])
      .filter((c) => c.name && link.columns.includes(c.name))
      .map((c) => [c.name as string, { label: c.label ?? null, unit: c.unit ?? null }])
  );
  return { type: "dataset", datasetId: dataset.id, columns: link.columns, columnInfo, window: reach, files };
}

/** One `model_output` input: the issues inside the window (HISTORICAL) or the latest N (FORECAST). */
export function buildModelOutputInputSample(args: {
  link: Pick<OutputLinkRow, "fieldKey" | "columns">;
  output: { id: string; unit?: string | null; outputLocation?: string | null };
  source: { id: string; s3BasePath?: string | null };
  records: ForecastRecordRow[];
  window: TimeWindow;
  pick: { window: TimeWindow } | { latest: number };
}): Record<string, unknown> {
  const { link, output, source, records, window, pick } = args;
  const prefix = output.outputLocation ? output.outputLocation.replace(/\/?$/, "/") : `${source.s3BasePath ?? ""}output/${output.id}/`;
  const issues: Array<InputIssue & { url: string }> = inputIssues(records, pick, prefix).map((issue) => ({
    ...issue,
    url: POST_SAMPLE_PLACEHOLDERS.url,
  }));
  const columnInfo = Object.fromEntries(link.columns.map((c) => [c, { label: null, unit: output.unit ?? null }]));
  return {
    type: "model_output",
    storageMode: "FILES",
    outputParamId: output.id,
    modelId: source.id,
    columns: link.columns,
    columnInfo,
    window,
    issues,
  };
}

/** historicalModelRunFunction's body for a CRON run of `window`; `targetRoutes`: DATASET output id → its dataset's routeLong. */
export function buildHistoricalPostSample(args: {
  schema: any;
  window: TimeWindow;
  inputs: Record<string, unknown>;
  targetRoutes: Record<string, string | null | undefined>;
  now: Date;
}): HistoricalPostSample {
  const { schema, window, inputs, targetRoutes, now } = args;
  const runId = POST_SAMPLE_PLACEHOLDERS.runId;
  const startedAt = now.toISOString();
  // Only DATASET outputs are run (historicalModelRunFunction): every historical output writes a dataset
  const outputs = (schema.outputs?.items || []).filter((o: any) => o?.storageMode === "DATASET").map((o: any, i: number) => {
    const uploadId = uploadIdFor(runId, i);
    const route = targetRoutes[o.id];
    const fileName = `${schema.name || schema.id} - ${o.name || o.id} - ${window.start.slice(0, 10)}.csv`;
    return {
      id: o.id,
      name: o.name,
      unit: o.unit,
      channels: o.channels,
      parameterTypeId: o.parameterTypeId,
      s3Key: route ? datasetIncomingKey(route, uploadId) : `<the target dataset's folder>/incoming/${uploadId}.csv`,
      metadata: datasetUploadMetadata({
        runId,
        inputsReadAt: startedAt,
        uploadId,
        ownerSub: POST_SAMPLE_PLACEHOLDERS.ownerSub,
        fileName,
      }),
    };
  });
  return {
    processedModelSchemaId: schema.id,
    schemaType: schema.schemaType,
    version: schema.version,
    modelKind: "HISTORICAL",
    schemaFields: safeParse(schema.schemaFields),
    window,
    trigger: { type: "CRON", ref: POST_SAMPLE_PLACEHOLDERS.scheduleId, requestId: POST_SAMPLE_PLACEHOLDERS.requestId },
    runId,
    attempt: 1,
    inputsReadAt: startedAt,
    inputs,
    outputs,
    linkedAssets: schema.linkedAssets?.items || [],
    linkedAssetGroups: flattenAssetGroups(schema),
    dataSources: schema.dataSources?.items || [],
    s3BasePath: schema.s3BasePath,
    s3Bucket: POST_SAMPLE_PLACEHOLDERS.s3Bucket,
    postgresSeriesFunction: POST_SAMPLE_PLACEHOLDERS.postgresSeriesFunction,
    owner: schema.owner ?? null,
    organizationId: schema.organizationId ?? null,
  };
}
